/**
 * MetaCampaignNew — tela "Nova campanha" da Meta (campanha + conjunto de anúncios).
 *
 * Quatro etapas: Campanha, Conjunto de anúncios, Anúncio e Revisão, com resumo fixo na lateral. A etapa de
 * revisão chama o backend sem `confirm` (ele valida tudo e devolve exatamente o que enviará à
 * Meta); só o botão "Criar campanha" envia de verdade. Campanha, conjunto e anúncio nascem pausados.
 * A mídia do anúncio é enviada à biblioteca da conta ao escolher o arquivo (isso não cria nem ativa nada).
 * As regras de objetivo/meta/destino vêm de `GET /meta-ads/manage/campaign/options`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import './metaPages.css'
import ActivityDeadlineField from '../comercial/ActivityDeadlineField.jsx'
import '../comercial/dealPanel.css'
import { useApp } from '../../context/AppContext.jsx'
import {
  createMetaCampaign, getMetaAdsAccounts, getMetaCampaignOptions, getMetaLeadForms, searchMetaTargeting,
  uploadMetaCampaignMedia,
} from '../../db/api.js'
import { useAccess } from '../../hooks/useAccess.js'
import { useApiData } from '../../hooks/useApiData.js'
import {
  AD_FORMATS, BID_STRATEGIES, CAPPED_BIDS, CONVERSION_EVENTS, DEVICES, FACEBOOK_POSITIONS, GENDERS, INSTAGRAM_POSITIONS,
  PLATFORMS, SPECIAL_CATEGORIES, STEPS, adRule, buildRequest, ctaChoices, describeSummary,
  draftSummary, emptyAd, emptyCard, emptyForm, goalChoices, goalRule, hasErrors, normalizeAd, normalizeAdset, stepErrors,
  successMessage,
} from '../../lib/metaCampaignForm.js'
import { demoCreate, demoLeadForms, demoOptions, demoSearch, demoUpload, isDemo } from '../../lib/metaCampaignDemo.js'
import { BUDGET_LABELS } from '../../lib/metaManage.js'
import { UTM_FIELDS, exampleLink, isEmptyTemplate } from '../../lib/metaUtm.js'

const BREADCRUMB = [
  { label: 'Marketing' },
  { label: 'Meta Ads', to: '/meta-ads' },
  { label: 'Campanhas', to: '/meta-ads/campanhas' },
  { label: 'Nova campanha' },
]

// ── Blocos de formulário ─────────────────────────────────────────────────────

function Field({ label, hint, error, children }) {
  return (
    <div className="field">
      {label && <label className="field-label">{label}</label>}
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="mcn-error" role="alert">{error}</div>}
    </div>
  )
}

function Card({ title, sub, children }) {
  return (
    <section className="mcn-card">
      <div className="mcn-card-title">{title}</div>
      {sub && <div className="mcn-card-sub">{sub}</div>}
      {!sub && <div className="mcn-gap-16" />}
      {children}
    </section>
  )
}

function Choices({ items, value, onChange, getKey = (i) => i.value }) {
  return (
    <div className="mcn-choices" role="radiogroup">
      {items.map((item) => {
        const key = getKey(item)
        return (
          <button
            type="button" key={key} role="radio" aria-checked={value === key}
            className={`mcn-choice${value === key ? ' is-selected' : ''}`} onClick={() => onChange(key)}
          >
            <span className="mcn-choice-title">{item.label}</span>
            {item.hint && <span className="mcn-choice-desc">{item.hint}</span>}
          </button>
        )
      })}
    </div>
  )
}

function CheckPills({ items, values, onChange }) {
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v])
  return (
    <div className="mcn-checks">
      {items.map((item) => (
        <label key={item.value} className={`mcn-check${values.includes(item.value) ? ' is-on' : ''}`}>
          <input type="checkbox" checked={values.includes(item.value)} onChange={() => toggle(item.value)} />
          {item.label}
        </label>
      ))}
    </div>
  )
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-slider" />
      <span className="toggle-label">{label}</span>
    </label>
  )
}

function Chips({ items, onRemove, render }) {
  if (!items.length) return null
  return (
    <div className="mcn-chips">
      {items.map((item, i) => (
        <span className="mcn-chip" key={`${i}-${item.key ?? item.id ?? item.code}`}>
          {render(item)}
          <button type="button" className="mcn-chip-x" aria-label="Remover" onClick={() => onRemove(i)}>×</button>
        </span>
      ))}
    </div>
  )
}

// Busca na Meta (locais ou interesses) com pausa de 350 ms; devolve o resultado escolhido.
function RemoteSearch({ kind, clientId, accountId, placeholder, onPick, search }) {
  const [text, setText] = useState('')
  const [results, setResults] = useState([])
  const [state, setState] = useState('idle')
  const seq = useRef(0)

  useEffect(() => {
    const q = text.trim()
    if (q.length < 2) { setResults([]); setState('idle'); return undefined }
    const id = ++seq.current
    setState('loading')
    const timer = setTimeout(() => {
      search({ clientId, accountId, kind, q })
        .then((data) => { if (id === seq.current) { setResults(data); setState('done') } })
        .catch(() => { if (id === seq.current) { setResults([]); setState('error') } })
    }, 350)
    return () => clearTimeout(timer)
  }, [text, kind, clientId, accountId, search])

  const pick = (item) => { onPick(item); setText(''); setResults([]); setState('idle') }
  const typeLabel = { country: 'País', region: 'Região', city: 'Cidade' }

  return (
    <div className="mcn-search">
      <input
        className="input" value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setText('')} aria-label={placeholder}
      />
      {state !== 'idle' && (
        <div className="mcn-results">
          {state === 'loading' && <div className="mcn-result">Buscando…</div>}
          {state === 'error' && <div className="mcn-result">Não foi possível buscar agora.</div>}
          {state === 'done' && results.length === 0 && <div className="mcn-result">Nada encontrado.</div>}
          {results.map((r) => (
            <button type="button" key={r.key ?? r.id} className="mcn-result" onClick={() => pick(r)}>
              <span>{r.name}{r.region && r.type === 'city' ? `, ${r.region}` : ''}</span>
              <small>{kind === 'geo' ? `${typeLabel[r.type] || ''}${r.country_code && r.type !== 'country' ? ` · ${r.country_code}` : ''}` : ''}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryRows({ rows }) {
  return (
    <dl className="mcn-dl">
      {rows.map(([label, value]) => (
        <div className="mcn-row" key={label}><dt>{label}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  )
}

// ── Etapa 1: Campanha ────────────────────────────────────────────────────────

function CampaignStep({ form, setForm, accounts, options, errors, onAccountChange }) {
  const c = form.campaign
  const set = (patch) => setForm((f) => {
    const campaign = { ...f.campaign, ...patch }
    const adset = 'objective' in patch ? normalizeAdset(options, campaign, f.adset) : f.adset
    return { ...f, campaign, adset }
  })
  const objectives = (options?.objectives || []).map((o) => ({ value: o.value, label: o.label }))

  return (
    <>
      <Card title="Conta e identificação" sub="Onde a campanha será criada e como ela se chama.">
        <div className="mcn-grid-2">
          <Field label="Conta de anúncios" error={errors.accountId}>
            <select className="input" value={form.accountId} onChange={(e) => onAccountChange(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </Field>
          <Field label="Nome da campanha" error={errors.name}>
            <input className="input" maxLength={200} value={c.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ex.: Black Friday 2026 — Vendas" />
          </Field>
        </div>
      </Card>

      <Card title="Objetivo" sub="O que você quer que as pessoas façam. Isso define as metas de otimização do conjunto.">
        {options ? (
          <Field error={errors.objective}>
            <Choices items={objectives} value={c.objective} onChange={(v) => set({ objective: v })} />
          </Field>
        ) : <div className="field-hint">Carregando as opções da conta…</div>}
      </Card>

      <Card title="Categoria especial de anúncios" sub="Marque se o anúncio trata de emprego, moradia, crédito ou política. A Meta restringe a segmentação nesses casos.">
        <CheckPills items={SPECIAL_CATEGORIES} values={c.specialCategories} onChange={(v) => set({ specialCategories: v })} />
        {c.specialCategories.length > 0 && (
          <div className="mcn-banner is-warn mcn-banner-tight">
            Com categoria especial não é possível filtrar por idade, gênero ou interesses no conjunto.
          </div>
        )}
      </Card>

      <Card title="Orçamento" sub="Onde o orçamento é definido. A escolha vale para esta campanha toda.">
        <Choices
          items={[
            { value: 'cbo', label: 'Na campanha (CBO)', hint: 'Orçamento da campanha: a Meta distribui o valor entre os conjuntos de anúncios.' },
            { value: 'abo', label: 'Em cada conjunto (ABO)', hint: 'Orçamento do conjunto: você define o valor de cada conjunto de anúncios.' },
          ]}
          value={c.budgetOnCampaign ? 'cbo' : 'abo'} onChange={(v) => set({ budgetOnCampaign: v === 'cbo' })}
        />
        {c.budgetOnCampaign && (
          <div className="mcn-grid-2 mcn-mt-16">
            <Field label="Tipo de orçamento">
              <select className="input" value={c.budgetKind} onChange={(e) => set({ budgetKind: e.target.value })}>
                {Object.entries(BUDGET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Valor" error={errors.amount}>
              <input className="input" inputMode="decimal" placeholder="Ex.: 120,00" value={c.amountText} onChange={(e) => set({ amountText: e.target.value })} />
            </Field>
          </div>
        )}
        <div className="mcn-mt-16">
          <Field label="Limite de gasto da campanha (opcional)" hint="A campanha para de entregar ao atingir esse total." error={errors.spendCap}>
            <input className="input mcn-w-240" inputMode="decimal" placeholder="Sem limite" value={c.spendCapText} onChange={(e) => set({ spendCapText: e.target.value })} />
          </Field>
        </div>
      </Card>
    </>
  )
}

// ── Etapa 2: Conjunto de anúncios ────────────────────────────────────────────

function AdsetStep({ form, setForm, options, errors, clientId, search }) {
  const { campaign: c, adset: a } = form
  const t = a.targeting
  const p = a.placements
  const goal = goalRule(options, c.objective, a.optimizationGoal)
  const goals = goalChoices(options, c.objective)
  const setAdset = (patch) => setForm((f) => ({ ...f, adset: { ...f.adset, ...patch } }))
  const setTargeting = (patch) => setForm((f) => ({ ...f, adset: { ...f.adset, targeting: { ...f.adset.targeting, ...patch } } }))
  const setPlacements = (patch) => setForm((f) => ({ ...f, adset: { ...f.adset, placements: { ...f.adset.placements, ...patch } } }))
  const pickGoal = (value) => setForm((f) => ({ ...f, adset: normalizeAdset(options, f.campaign, { ...f.adset, optimizationGoal: value, destinationType: '' }) }))
  const showDestination = goal && goal.destinations.length > 1
  const destinations = goal ? goal.destinations.map((d) => ({ value: d, label: options.destinations[d] || d })) : []

  const addPlace = (r) => {
    if (r.type === 'country') {
      const code = r.country_code || r.key
      if (!t.countries.some((x) => x.code === code)) setTargeting({ countries: [...t.countries, { code, name: r.name }] })
    } else if (r.type === 'region') {
      if (!t.regions.some((x) => x.key === r.key)) setTargeting({ regions: [...t.regions, { key: r.key, name: r.name }] })
    } else if (!t.cities.some((x) => x.key === r.key)) {
      setTargeting({ cities: [...t.cities, { key: r.key, name: r.name, radiusKm: 17 }] })
    }
  }
  const removeAt = (field, index) => setTargeting({ [field]: t[field].filter((_, i) => i !== index) })
  const setRadius = (index, radiusKm) => setTargeting({ cities: t.cities.map((x, i) => (i === index ? { ...x, radiusKm } : x)) })
  const toggleAudience = (field, audience) => {
    const list = t[field]
    setTargeting({ [field]: list.some((x) => x.id === audience.id) ? list.filter((x) => x.id !== audience.id) : [...list, { id: audience.id, name: audience.name }] })
  }
  const audiences = options?.audiences || []

  return (
    <>
      {errors.special && <div className="mcn-banner is-error" role="alert">{errors.special}</div>}

      <Card title="Identificação e meta" sub="Como o conjunto se chama e o resultado que a Meta deve buscar.">
        <Field label="Nome do conjunto de anúncios" error={errors.name}>
          <input className="input" maxLength={200} value={a.name} onChange={(e) => setAdset({ name: e.target.value })} placeholder="Ex.: Brasil — 25 a 45 anos" />
        </Field>
        <Field label="Meta de otimização" error={errors.goal}>
          <Choices items={goals} value={a.optimizationGoal} onChange={pickGoal} />
        </Field>
        {showDestination && (
          <Field label="Onde as pessoas vão">
            <Choices items={destinations} value={a.destinationType} onChange={(v) => setAdset({ destinationType: v })} />
          </Field>
        )}
        {goal?.needs_page && (
          <Field label="Página do Facebook" error={errors.page} hint={options.pages.length ? null : 'Nenhuma Página disponível nesta conta.'}>
            <select className="input" value={a.pageId} onChange={(e) => setAdset({ pageId: e.target.value })}>
              <option value="">Selecione…</option>
              {options.pages.map((pg) => <option key={pg.id} value={pg.id}>{pg.name}</option>)}
            </select>
          </Field>
        )}
        {goal?.needs_pixel && (
          <div className="mcn-grid-2">
            <Field label="Pixel" error={errors.pixel} hint={options.pixels.length ? null : 'Nenhum pixel encontrado nesta conta.'}>
              <select className="input" value={a.pixelId} onChange={(e) => setAdset({ pixelId: e.target.value })}>
                <option value="">Selecione…</option>
                {options.pixels.map((px) => <option key={px.id} value={px.id}>{px.name}</option>)}
              </select>
            </Field>
            <Field label="Evento de conversão">
              <select className="input" value={a.eventType} onChange={(e) => setAdset({ eventType: e.target.value })}>
                {CONVERSION_EVENTS.map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
              </select>
            </Field>
          </div>
        )}
      </Card>

      <Card title="Orçamento, cronograma e lance" sub={c.budgetOnCampaign ? 'O orçamento está na campanha (CBO); aqui você define o período e o lance.' : 'Orçamento do conjunto (ABO): quanto gastar neste conjunto e por quanto tempo.'}>
        {!c.budgetOnCampaign && (
          <div className="mcn-grid-2">
            <Field label="Tipo de orçamento">
              <select className="input" value={a.budgetKind} onChange={(e) => setAdset({ budgetKind: e.target.value })}>
                {Object.entries(BUDGET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Valor" error={errors.amount}>
              <input className="input" inputMode="decimal" placeholder="Ex.: 120,00" value={a.amountText} onChange={(e) => setAdset({ amountText: e.target.value })} />
            </Field>
          </div>
        )}
        <div className="mcn-grid-2">
          <Field label="Início" hint="Vazio = assim que o conjunto for ativado.">
            <ActivityDeadlineField
              value={a.startTime} onChange={(v) => setAdset({ startTime: v })}
              label="Início do conjunto" placeholder="Assim que for ativado" clearLabel="Sem início" timeLabel="Horário de início"
            />
          </Field>
          <Field label="Término" error={errors.endTime} hint="Obrigatório no orçamento vitalício.">
            <ActivityDeadlineField
              value={a.endTime} onChange={(v) => setAdset({ endTime: v })}
              label="Término do conjunto" placeholder="Sem data de término" clearLabel="Sem término" timeLabel="Horário de término"
            />
          </Field>
        </div>
        <Field label="Estratégia de lance">
          <Choices items={BID_STRATEGIES} value={a.bidStrategy} onChange={(v) => setAdset({ bidStrategy: v })} />
        </Field>
        {CAPPED_BIDS.includes(a.bidStrategy) && (
          <Field label={a.bidStrategy === 'COST_CAP' ? 'Meta de custo por resultado' : 'Lance máximo'} error={errors.bidAmount}>
            <input className="input mcn-w-240" inputMode="decimal" placeholder="Ex.: 12,50" value={a.bidAmountText} onChange={(e) => setAdset({ bidAmountText: e.target.value })} />
          </Field>
        )}
      </Card>

      <Card title="Público" sub="Quem deve ver os anúncios.">
        <Field label="Locais" error={errors.locations} hint="Busque países, regiões ou cidades.">
          <RemoteSearch kind="geo" search={search} clientId={clientId} accountId={form.accountId} placeholder="Buscar local (ex.: Brasil, São Paulo)" onPick={addPlace} />
          <Chips items={t.countries} onRemove={(i) => removeAt('countries', i)} render={(x) => <>{x.name || x.code} <small>(país)</small></>} />
          <Chips items={t.regions} onRemove={(i) => removeAt('regions', i)} render={(x) => <>{x.name || x.key} <small>(região)</small></>} />
          <Chips
            items={t.cities} onRemove={(i) => removeAt('cities', i)}
            render={(x) => {
              const index = t.cities.indexOf(x)
              return (
                <>
                  {x.name || x.key}
                  <select aria-label={`Raio de ${x.name}`} value={x.radiusKm} onChange={(e) => setRadius(index, Number(e.target.value))} className="mcn-radius-select">
                    {[17, 25, 40, 50, 80].map((km) => <option key={km} value={km}>+{km} km</option>)}
                  </select>
                </>
              )
            }}
          />
        </Field>

        <div className="mcn-grid-3">
          <Field label="Idade mínima" error={errors.age}>
            <input className="input" type="number" min={13} max={65} value={t.ageMin} onChange={(e) => setTargeting({ ageMin: Number(e.target.value) || 18 })} />
          </Field>
          <Field label="Idade máxima">
            <input className="input" type="number" min={13} max={65} value={t.ageMax} onChange={(e) => setTargeting({ ageMax: Number(e.target.value) || 65 })} />
          </Field>
          <Field label="Gênero">
            <select className="input" value={t.genders} onChange={(e) => setTargeting({ genders: e.target.value })}>
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Interesses" hint="Detalhamento do público por interesses. Opcional.">
          <RemoteSearch
            kind="interest" search={search} clientId={clientId} accountId={form.accountId} placeholder="Buscar interesse (ex.: moda, fitness)"
            onPick={(r) => !t.interests.some((x) => x.id === r.id) && setTargeting({ interests: [...t.interests, { id: r.id, name: r.name }] })}
          />
          <Chips items={t.interests} onRemove={(i) => removeAt('interests', i)} render={(x) => x.name} />
        </Field>

        <div className="mcn-grid-2">
          <Field label="Públicos personalizados (incluir)" hint={audiences.length ? null : 'Esta conta não tem públicos personalizados.'}>
            <div className="mcn-checks">
              {audiences.map((au) => (
                <label key={au.id} className={`mcn-check${t.customAudiences.some((x) => x.id === au.id) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={t.customAudiences.some((x) => x.id === au.id)} onChange={() => toggleAudience('customAudiences', au)} />
                  {au.name}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Públicos personalizados (excluir)">
            <div className="mcn-checks">
              {audiences.map((au) => (
                <label key={au.id} className={`mcn-check${t.excludedAudiences.some((x) => x.id === au.id) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={t.excludedAudiences.some((x) => x.id === au.id)} onChange={() => toggleAudience('excludedAudiences', au)} />
                  {au.name}
                </label>
              ))}
            </div>
          </Field>
        </div>

        <Toggle checked={t.advantageAudience} onChange={(v) => setTargeting({ advantageAudience: v })} label="Público Advantage+ (a Meta pode ampliar o público além das suas escolhas)" />
      </Card>

      <Card title="Posicionamentos" sub="Onde os anúncios aparecem.">
        <Choices
          items={[
            { value: 'automatic', label: 'Automático (recomendado)', hint: 'A Meta distribui entre todos os posicionamentos.' },
            { value: 'manual', label: 'Manual', hint: 'Você escolhe plataformas, posições e dispositivos.' },
          ]}
          value={p.mode} onChange={(v) => setPlacements({ mode: v })}
        />
        {p.mode === 'manual' && (
          <div className="mcn-mt-18">
            <Field label="Plataformas" error={errors.placements}>
              <CheckPills items={PLATFORMS} values={p.platforms} onChange={(v) => setPlacements({ platforms: v })} />
            </Field>
            {p.platforms.includes('facebook') && (
              <Field label="Posições no Facebook" hint="Vazio = todas.">
                <CheckPills items={FACEBOOK_POSITIONS} values={p.facebook} onChange={(v) => setPlacements({ facebook: v })} />
              </Field>
            )}
            {p.platforms.includes('instagram') && (
              <Field label="Posições no Instagram" hint="Vazio = todas.">
                <CheckPills items={INSTAGRAM_POSITIONS} values={p.instagram} onChange={(v) => setPlacements({ instagram: v })} />
              </Field>
            )}
            <Field label="Dispositivos" error={errors.devices}>
              <CheckPills items={DEVICES} values={p.devices} onChange={(v) => setPlacements({ devices: v })} />
            </Field>
          </div>
        )}
      </Card>
    </>
  )
}

// ── Etapa 3: Anúncio ─────────────────────────────────────────────────────────

// Escolhe e envia o arquivo à biblioteca da conta. O preview da imagem é local (blob), não vem da Meta.
function MediaUpload({ kind, value, onChange, upload, clientId, accountId, limitMb, label, error }) {
  const input = useRef(null)
  const [state, setState] = useState({ busy: false, error: null })

  async function pick(event) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > limitMb * 1024 * 1024) {
      setState({ busy: false, error: `O arquivo passa de ${limitMb} MB.` })
      return
    }
    setState({ busy: true, error: null })
    try {
      const res = await upload({ clientId, accountId, kind, file })
      if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl)
      onChange({ id: res.id, name: file.name, previewUrl: kind === 'image' ? URL.createObjectURL(file) : null })
      setState({ busy: false, error: null })
    } catch (err) {
      setState({ busy: false, error: err.message || 'Não foi possível enviar o arquivo.' })
    }
  }

  return (
    <div className="mcn-media">
      <div className="mcn-media-thumb" aria-hidden="true">
        {value?.previewUrl ? <img src={value.previewUrl} alt="" /> : <span>{value ? 'Vídeo' : kind === 'image' ? 'Imagem' : 'Vídeo'}</span>}
      </div>
      <div className="mcn-media-body">
        <div className="field-label mcn-mb-4">{label}</div>
        <div className="mcn-card-sub mcn-m-0">
          {value ? value.name : kind === 'image' ? `JPG ou PNG, até ${limitMb} MB.` : `MP4 ou MOV, até ${limitMb} MB.`}
        </div>
        <div className="mcn-actions-row">
          <button type="button" className="btn btn-outline btn-sm" disabled={state.busy} onClick={() => input.current?.click()}>
            {state.busy ? 'Enviando…' : value ? 'Trocar arquivo' : 'Escolher arquivo'}
          </button>
          {value && !state.busy && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)}>Remover</button>
          )}
        </div>
        {(state.error || error) && <div className="mcn-error" role="alert">{state.error || error}</div>}
      </div>
      <input
        ref={input} type="file" hidden onChange={pick}
        accept={kind === 'image' ? 'image/png,image/jpeg' : 'video/mp4,video/quicktime'}
      />
    </div>
  )
}

function AdStep({ form, setForm, options, errors, clientId, upload, loadForms }) {
  const navigate = useNavigate()
  const { adset, ad } = form
  const rule = adRule(options, adset.destinationType)
  const setAd = (patch) => setForm((f) => ({ ...f, ad: { ...f.ad, ...patch } }))
  const setUtm = (patch) => setForm((f) => ({ ...f, ad: { ...f.ad, utm: { ...f.ad.utm, ...patch } } }))
  const setCard = (index, patch) => setForm((f) => ({
    ...f, ad: { ...f.ad, cards: f.ad.cards.map((c, i) => (i === index ? { ...c, ...patch } : c)) },
  }))
  const limits = { image: options?.ad?.image_max_mb || 10, video: options?.ad?.video_max_mb || 100 }
  const mediaProps = { upload, clientId, accountId: form.accountId }
  const [forms, setForms] = useState({ list: [], warning: null })

  useEffect(() => {
    if (!rule.needs_form || !ad.pageId) return undefined
    let alive = true
    loadForms({ clientId, accountId: form.accountId, pageId: ad.pageId })
      .then((res) => { if (alive) setForms({ list: res.forms || [], warning: res.warning || null }) })
      .catch(() => { if (alive) setForms({ list: [], warning: 'Não foi possível listar os formulários.' }) })
    return () => { alive = false }
  }, [rule.needs_form, ad.pageId, clientId, form.accountId, loadForms])

  const pageLocked = Boolean(adset.pageId)

  return (
    <>
      <Card title="Identidade" sub="Quem aparece como autor do anúncio.">
        <Field label="Nome do anúncio" error={errors.name}>
          <input className="input" maxLength={200} value={ad.name} onChange={(e) => setAd({ name: e.target.value })} placeholder="Ex.: Oferta de lançamento — imagem 1" />
        </Field>
        <div className="mcn-grid-2">
          <Field label="Página do Facebook" error={errors.page} hint={pageLocked ? 'Igual à Página promovida no conjunto.' : null}>
            <select className="input" value={ad.pageId} disabled={pageLocked} onChange={(e) => setAd({ pageId: e.target.value })}>
              <option value="">Selecione…</option>
              {(options?.pages || []).map((pg) => <option key={pg.id} value={pg.id}>{pg.name}</option>)}
            </select>
          </Field>
          <Field label="Conta do Instagram (opcional)">
            <select className="input" value={ad.instagramId} onChange={(e) => setAd({ instagramId: e.target.value })}>
              <option value="">Usar só a Página</option>
              {(options?.instagram || []).map((ig) => <option key={ig.id} value={ig.id}>@{ig.name}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Formato e mídia" sub="O que as pessoas vão ver. A mídia fica na biblioteca da conta; nada é publicado antes da criação.">
        <Field>
          <Choices items={AD_FORMATS} value={ad.format} onChange={(v) => setAd({ format: v })} />
        </Field>
        {ad.format === 'image' && (
          <MediaUpload
            {...mediaProps} kind="image" label="Imagem" limitMb={limits.image} value={ad.image}
            onChange={(v) => setAd({ image: v })} error={errors.media}
          />
        )}
        {ad.format === 'video' && (
          <div className="mcn-grid-2">
            <MediaUpload
              {...mediaProps} kind="video" label="Vídeo" limitMb={limits.video} value={ad.video}
              onChange={(v) => setAd({ video: v })} error={errors.media}
            />
            <MediaUpload
              {...mediaProps} kind="image" label="Capa do vídeo" limitMb={limits.image} value={ad.thumbnail}
              onChange={(v) => setAd({ thumbnail: v })} error={errors.thumbnail}
            />
          </div>
        )}
        {ad.format === 'carousel' && (
          <>
            {errors.cards && <div className="mcn-error" role="alert">{errors.cards}</div>}
            {ad.cards.map((card, index) => (
              <div className="mcn-card-item" key={index}>
                <div className="mcn-card-item-head">
                  <strong>Cartão {index + 1}</strong>
                  {ad.cards.length > 2 && (
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setAd({ cards: ad.cards.filter((_, i) => i !== index) })}>Remover</button>
                  )}
                </div>
                <MediaUpload
                  {...mediaProps} kind="image" label="Imagem" limitMb={limits.image} value={card.image}
                  onChange={(v) => setCard(index, { image: v })} error={errors[`card${index}`]}
                />
                <div className="mcn-grid-2 mcn-mt-14">
                  <Field label="Título">
                    <input className="input" maxLength={255} value={card.headline} onChange={(e) => setCard(index, { headline: e.target.value })} />
                  </Field>
                  <Field label={rule.needs_link ? 'Link do cartão' : 'Link do cartão (opcional)'}>
                    <input className="input" maxLength={2000} value={card.link} onChange={(e) => setCard(index, { link: e.target.value })} placeholder="https://" />
                  </Field>
                </div>
                <Field label="Descrição">
                  <input className="input" maxLength={255} value={card.description} onChange={(e) => setCard(index, { description: e.target.value })} />
                </Field>
              </div>
            ))}
            {ad.cards.length < 10 && (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setAd({ cards: [...ad.cards, emptyCard()] })}>Adicionar cartão</button>
            )}
          </>
        )}
      </Card>

      <Card title="Texto e botão" sub="O que o anúncio diz e para onde leva.">
        <Field label={ad.format === 'carousel' ? 'Texto principal (opcional)' : 'Texto principal'} error={errors.message}>
          <textarea className="input" rows={4} maxLength={5000} value={ad.message} onChange={(e) => setAd({ message: e.target.value })} placeholder="Texto que aparece acima da mídia" />
        </Field>
        {ad.format !== 'carousel' && (
          <div className="mcn-grid-2">
            <Field label="Título">
              <input className="input" maxLength={255} value={ad.headline} onChange={(e) => setAd({ headline: e.target.value })} />
            </Field>
            <Field label="Descrição">
              <input className="input" maxLength={255} value={ad.description} onChange={(e) => setAd({ description: e.target.value })} />
            </Field>
          </div>
        )}
        {ad.format !== 'carousel' && (
          <div className="mcn-grid-2">
            <Field label={rule.needs_link ? 'Link de destino' : 'Link de destino (opcional)'} error={errors.link}>
              <input className="input" maxLength={2000} value={ad.link} onChange={(e) => setAd({ link: e.target.value })} placeholder="https://" />
            </Field>
            <Field label="Link de exibição (opcional)" hint="Texto mostrado no lugar do endereço, ex.: loja.com.br.">
              <input className="input" maxLength={255} value={ad.displayLink} onChange={(e) => setAd({ displayLink: e.target.value })} />
            </Field>
          </div>
        )}
        <Field label="Botão de ação" error={errors.cta}>
          <select className="input mcn-w-320" value={ad.cta} onChange={(e) => setAd({ cta: e.target.value })}>
            {ctaChoices(options, adset.destinationType).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        {rule.needs_form && (
          <Field label="Formulário de lead" error={errors.leadForm} hint={forms.warning || (forms.list.length ? null : 'Nenhum formulário encontrado nesta Página.')}>
            {forms.list.length > 0 ? (
              <select className="input" value={ad.leadFormId} onChange={(e) => setAd({ leadFormId: e.target.value })}>
                <option value="">Selecione…</option>
                {forms.list.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            ) : (
              <input className="input mcn-w-320" inputMode="numeric" value={ad.leadFormId} onChange={(e) => setAd({ leadFormId: e.target.value.replace(/\D/g, '') })} placeholder="ID do formulário" />
            )}
          </Field>
        )}
      </Card>

      <Card
        title="Rastreamento (opcional)"
        sub={options?.utm_template
          ? 'Parâmetros UTM somados ao link de destino. Vieram do modelo do cliente e podem ser ajustados neste anúncio.'
          : 'Parâmetros UTM somados ao link de destino. Dica: salve um modelo do cliente para não digitar em todo anúncio.'}
        aside={<button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/meta-ads/utms')}>Editar modelo</button>}
      >
        <div className="mcn-grid-3">
          {UTM_FIELDS.map(({ key, param, label }) => (
            <Field label={`${label} (${param})`} key={key} error={errors[`utm_${key}`]}>
              <input className="input" maxLength={100} value={ad.utm[key]} onChange={(e) => setUtm({ [key]: e.target.value })} />
            </Field>
          ))}
        </div>
        <div className="field-hint">
          Valores entre {'{{ }}'}, como {'{{campaign.name}}'}, são trocados pela Meta no clique.
        </div>
        {!isEmptyTemplate(ad.utm) && (
          <div className="mcn-example">
            <div className="field-label mcn-mb-4">Link final de exemplo</div>
            <code>{exampleLink(ad.link, ad.utm)}</code>
          </div>
        )}
      </Card>
    </>
  )
}

// Prévia local da mídia na revisão (os arquivos já estão na conta; aqui só mostramos o que foi escolhido).
function MediaReview({ ad }) {
  const items = ad.format === 'carousel'
    ? ad.cards.map((c, i) => ({ key: i, media: c.image, caption: c.headline || `Cartão ${i + 1}` }))
    : [
      { key: 'main', media: ad.format === 'video' ? ad.video : ad.image, caption: ad.format === 'video' ? 'Vídeo' : 'Imagem' },
      ...(ad.format === 'video' ? [{ key: 'thumb', media: ad.thumbnail, caption: 'Capa' }] : []),
    ]
  return (
    <section className="mcn-card">
      <div className="mcn-card-title">Mídia do anúncio</div>
      <div className="mcn-gap-12" />
      <div className="mcn-review-media">
        {items.map((item) => (
          <figure key={item.key} className="mcn-review-media-item">
            <div className="mcn-media-thumb">
              {item.media?.previewUrl ? <img src={item.media.previewUrl} alt="" /> : <span>{item.media ? 'Vídeo' : '—'}</span>}
            </div>
            <figcaption>{item.caption}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  )
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function MetaCampaignNew() {
  const navigate = useNavigate()
  const demo = isDemo(useLocation().search)
  const { activeClient } = useApp()
  const { isOrgAdmin } = useAccess()
  const clientId = activeClient?.id ?? null

  const { data: accountsData, loading: accountsLoading } = useApiData(getMetaAdsAccounts, [clientId], { initialData: [] })
  const accounts = useMemo(() => (accountsData || []).filter((a) => !clientId || String(a.client_id) === String(clientId)), [accountsData, clientId])

  const [form, setForm] = useState(() => emptyForm())
  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null)
  const [step, setStep] = useState(0)
  const [reached, setReached] = useState(0)
  const [showErrors, setShowErrors] = useState(false)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // Conta padrão: a primeira do cliente.
  useEffect(() => {
    if (!form.accountId && accounts.length) setForm((f) => ({ ...f, accountId: String(accounts[0].id) }))
  }, [accounts, form.accountId])

  // Regras e dados da conta (Páginas, pixels, públicos) a cada troca de conta.
  useEffect(() => {
    if (!clientId || !form.accountId) return undefined
    let alive = true
    setOptions(null)
    setOptionsError(null)
    ;(demo ? demoOptions() : getMetaCampaignOptions({ clientId, accountId: form.accountId }))
      .then((data) => { if (alive) setOptions(data) })
      .catch((err) => { if (alive) setOptionsError(err.message || 'Não foi possível carregar as opções da conta.') })
    return () => { alive = false }
  }, [clientId, form.accountId, demo])

  const account = accounts.find((a) => String(a.id) === String(form.accountId))
  const currency = options?.currency || account?.currency || 'BRL'
  const stepId = STEPS[step].id
  const errors = stepErrors(stepId, form, options)

  const changeAccount = useCallback((accountId) => {
    setForm((f) => ({
      ...emptyForm(accountId),
      campaign: { ...f.campaign, objective: '' },
      adset: { ...f.adset, optimizationGoal: '', destinationType: '', pageId: '', pixelId: '', targeting: { ...f.adset.targeting, customAudiences: [], excludedAudiences: [] } },
      ad: emptyAd(),
    }))
  }, [])

  const sidebar = useMemo(
    () => describeSummary(draftSummary(form), { options, currency, accountName: account?.name }),
    [form, options, currency, account],
  )

  function next() {
    if (hasErrors(errors)) { setShowErrors(true); return }
    setShowErrors(false)
    const target = step + 1
    if (STEPS[target].id === 'ad') setForm((f) => ({ ...f, ad: normalizeAd(options, f.adset, f.ad) }))
    setStep(target)
    setReached((r) => Math.max(r, target))
    if (STEPS[target].id === 'review') loadPreview()
    window.scrollTo?.({ top: 0 })
  }

  function goTo(index) {
    if (index > reached || busy) return
    setShowErrors(false)
    setStep(index)
    if (STEPS[index].id === 'review') loadPreview()
  }

  const send = (confirm) => (demo ? demoCreate(form, confirm) : createMetaCampaign(buildRequest(form, clientId, confirm)))
  const search = demo ? demoSearch : searchMetaTargeting
  const upload = demo ? demoUpload : uploadMetaCampaignMedia
  const loadForms = demo ? demoLeadForms : getMetaLeadForms

  async function loadPreview() {
    setBusy(true)
    setError(null)
    setPreview(null)
    try {
      setPreview((await send(false)).preview)
    } catch (err) {
      setError(err.message || 'Não foi possível validar a campanha.')
    } finally {
      setBusy(false)
    }
  }

  async function create() {
    setBusy(true)
    setError(null)
    try {
      const res = await send(true)
      setResult(res.preview)
    } catch (err) {
      setError(err.message || 'A campanha não foi criada.')
    } finally {
      setBusy(false)
    }
  }

  const reviewSections = useMemo(
    () => (preview ? describeSummary(preview.after, { options, currency, accountName: account?.name }) : []),
    [preview, options, currency, account],
  )

  // ── Estados de página ──
  let body
  if (!isOrgAdmin) {
    body = <div className="mcn-banner is-warn">Apenas owner e admin da organização podem criar campanhas.</div>
  } else if (!clientId) {
    body = <div className="mcn-banner">Selecione um cliente no topo da tela para criar uma campanha.</div>
  } else if (!accountsLoading && accounts.length === 0) {
    body = <div className="mcn-banner">Este cliente não tem conta de anúncios da Meta vinculada.</div>
  } else if (optionsError) {
    body = (
      <>
        <div className="mcn-banner is-error" role="alert">{optionsError}</div>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/meta-ads/campanhas')}>Voltar às campanhas</button>
      </>
    )
  } else if (result) {
    body = (
      <section className="mcn-card mcn-done">
        <div className="mcn-done-icon" aria-hidden="true">✓</div>
        <div className="mcn-card-title mcn-title-lg">Campanha criada</div>
        <p className="mcn-card-sub mcn-mb-20">
          {successMessage({ after: result.after })} Ative quando estiver pronta. Ela aparece na tabela depois da próxima coleta.
        </p>
        <div className="mcn-actions-center">
          <button type="button" className="btn btn-outline" onClick={() => navigate('/meta-ads/campanhas')}>Ver campanhas</button>
          <button
            type="button" className="btn btn-black"
            onClick={() => { setResult(null); setPreview(null); setStep(0); setReached(0); setForm(emptyForm(form.accountId)) }}
          >
            Criar outra
          </button>
        </div>
      </section>
    )
  } else {
    body = (
      <>
        {demo && (
          <div className="mcn-banner is-warn">
            Modo demonstração (somente desenvolvimento): nada é enviado à Meta e as opções da conta são de exemplo.
          </div>
        )}
        <div className="mcn-steps" role="tablist">
          {STEPS.map((s, i) => (
            <button
              type="button" key={s.id} role="tab" aria-selected={i === step} disabled={i > reached}
              className={`mcn-step${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`} onClick={() => goTo(i)}
            >
              <span className="mcn-step-num">{i < step ? '✓' : i + 1}</span>
              <span><div className="mcn-step-label">{s.label}</div><div className="mcn-step-hint">{s.hint}</div></span>
            </button>
          ))}
        </div>

        <div className="mcn-layout">
          <div className="mcn-main">
            {stepId === 'campaign' && (
              <CampaignStep form={form} setForm={setForm} accounts={accounts} options={options} errors={showErrors ? errors : {}} onAccountChange={changeAccount} />
            )}
            {stepId === 'adset' && options && (
              <AdsetStep form={form} setForm={setForm} options={options} errors={showErrors ? errors : {}} clientId={clientId} search={search} />
            )}
            {stepId === 'ad' && options && (
              <AdStep form={form} setForm={setForm} options={options} errors={showErrors ? errors : {}} clientId={clientId} upload={upload} loadForms={loadForms} />
            )}
            {stepId === 'review' && (
              <>
                {error && <div className="mcn-banner is-error" role="alert">{error}</div>}
                {busy && !preview && <div className="mcn-banner">Validando os dados na Meta…</div>}
                {preview && (
                  <>
                    <div className="mcn-banner">
                      Revise tudo abaixo. Nada foi enviado à Meta ainda: a campanha e o conjunto só são criados ao clicar em <strong>Criar campanha</strong>, e nascem <strong>pausados</strong>.
                    </div>
                    <MediaReview ad={form.ad} />
                    {reviewSections.map((sec) => (
                      <section className="mcn-card mcn-review-sec" key={sec.title}>
                        <div className="mcn-card-title">{sec.title}</div>
                        <div className="mcn-gap-12" />
                        <SummaryRows rows={sec.rows} />
                      </section>
                    ))}
                  </>
                )}
              </>
            )}

            {showErrors && hasErrors(errors) && (
              <div className="mcn-banner is-error" role="alert">Corrija os campos destacados para continuar.</div>
            )}

            <div className="mcn-footer">
              {step === 0
                ? <button type="button" className="btn btn-ghost" onClick={() => navigate('/meta-ads/campanhas')}>Cancelar</button>
                : <button type="button" className="btn btn-ghost" onClick={() => goTo(step - 1)} disabled={busy}>Voltar</button>}
              {stepId !== 'review'
                ? <button type="button" className="btn btn-black" onClick={next} disabled={!options}>Continuar</button>
                : <button type="button" className="btn btn-black" onClick={create} disabled={busy || !preview}>{busy && preview ? 'Criando…' : 'Criar campanha'}</button>}
            </div>
          </div>

          <aside className="mcn-aside" aria-label="Resumo da campanha">
            <div className="mcn-summary">
              {sidebar.slice(0, 3).map((sec) => (
                <div className="mcn-summary-sec" key={sec.title}>
                  <h4>{sec.title}</h4>
                  <SummaryRows rows={sec.rows.slice(0, 5)} />
                </div>
              ))}
            </div>
          </aside>
        </div>
      </>
    )
  }

  // Sem período: é formulário de criação, não relatório por data.
  return (
    <Layout title="Meta Ads — Nova campanha" breadcrumb={BREADCRUMB}>
      <div className="mcn">{body}</div>
    </Layout>
  )
}
